import { useMemo } from 'react';

import {
  InfoIcon,
  FiberIcon,
  RemoveCircleIcon,
  OpenInNewIcon,
  CommentIcon,
  PermMediaIcon,
  PhotoLibraryIcon,
  VideocamIcon,
  PeopleIcon,
  StarOutlineIcon,
  GifBoxIcon,
  PlayCircleFilledIcon,
  ImageIcon,
  SwitchVideoIcon,
  PeopleOutlineIcon,
  PublicIcon,
  InfoOutlinedIcon,
  ComputerIcon,
  WebOutlinedIcon,
  AutoAwesomeIcon,
} from '@gogame/components';
import { useFormat, dateFNS, toDate } from '@gogame/intl-ts';

import {
  CREATIVE_TYPE_BANNER,
  CREATIVE_TYPE_CINEMAGRAPH,
  CREATIVE_TYPE_GIF,
  CREATIVE_TYPE_PLAYABLE,
  CREATIVE_TYPE_VIDEO,
} from 'scenes/Gallery/GalleryPage/constants';

export const useAds = () => {
  return useMemo(
    () => ({
      icon: <OpenInNewIcon />,
      label: 'Ads',
      variant: 'outlined',
      color: 'primary',
      typographyVariant: 'caption',
      typographyColor: 'primary',
    }),
    []
  );
};

export const useCommentsCount = ({ value }) => {
  return useMemo(
    () => ({
      icon: <CommentIcon />,
      label: value,
      variant: 'outlined',
      color: 'action',
    }),
    [value]
  );
};

export const useConceptsCount = ({ value }) => {
  return useMemo(
    () => ({
      icon: <PermMediaIcon />,
      label: `${value} concepts`,
      shortLabel: value,
      color: 'action',
    }),
    [value]
  );
};

export const useCreativesCount = ({ value }) => {
  return useMemo(
    () => ({
      icon: <PhotoLibraryIcon />,
      label: `${value} creatives`,
      shortLabel: value,
      color: 'action',
    }),
    [value]
  );
};

export const useVariantsCount = ({ value }) => {
  return useMemo(
    () => ({
      icon: <PhotoLibraryIcon />,
      label: `${value} variants`,
      shortLabel: value,
      color: 'action',
    }),
    [value]
  );
};

export const useCreativeType = ({ value }) => {
  return useMemo(() => {
    const commonProps = {
      color: 'action',
    };

    switch (value) {
      case CREATIVE_TYPE_VIDEO: {
        return { icon: <VideocamIcon />, label: 'Video', ...commonProps };
      }

      case CREATIVE_TYPE_PLAYABLE: {
        return {
          icon: <PlayCircleFilledIcon />,
          label: 'Playable',
          ...commonProps,
        };
      }

      case CREATIVE_TYPE_BANNER: {
        return { icon: <ImageIcon />, label: 'Banner', ...commonProps };
      }

      case CREATIVE_TYPE_CINEMAGRAPH: {
        return {
          icon: <SwitchVideoIcon />,
          label: 'Cinemagraph',
          ...commonProps,
        };
      }

      case CREATIVE_TYPE_GIF: {
        return { icon: <GifBoxIcon />, label: 'Gif', ...commonProps };
      }

      default: {
        return {};
      }
    }
  }, [value]);
};

export const useFileExtension = ({ value }) => {
  return useMemo(
    () => ({
      label: `${value}`.replace('.', ''),
    }),
    [value]
  );
};

export const useFileResolution = ({ value }) => {
  const { width, height } = value;

  return useMemo(
    () => ({
      label: `${width}x${height}`,
    }),
    [width, height]
  );
};

export const useFileSize = ({ value }) => {
  return useMemo(
    () => ({
      label: `${(value / (1024 * 1024)).toFixed(2)} MB`,
    }),
    [value]
  );
};

export const useFileVideoLength = ({ value }) => {
  return useMemo(
    () => ({
      label: (value - (value %= 60)) / 60 + (9 < value ? ':' : ':0') + value,
    }),
    [value]
  );
};

export const usePartialShared = () => {
  return useMemo(
    () => ({
      icon: <PeopleOutlineIcon />,
      label: 'Shared partially',
      color: 'action',
    }),
    []
  );
};

export const useShared = () => {
  return useMemo(
    () => ({
      icon: <PeopleIcon />,
      label: 'Shared',
      color: 'action',
    }),
    []
  );
};

export const useRating = ({ value }) => {
  const format = useFormat({
    type: 'decimal',
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  });

  return useMemo(
    () => ({
      icon: <StarOutlineIcon />,
      label: format(value),
      color: 'action',
    }),
    [value]
  );
};

export const useNew = () => {
  return useMemo(
    () => ({
      icon: <FiberIcon />,
      label: 'Is new',
      color: 'success',
    }),
    []
  );
};

export const useExpirationDate = ({ value }) => {
  const diffDays = useMemo(
    () =>
      dateFNS.differenceInDays(
        dateFNS.startOfDay(toDate(value)),
        dateFNS.startOfDay(new Date())
      ),
    [value]
  );

  return useMemo(() => {
    const commonProps = {
      icon: <InfoIcon />,
    };

    switch (true) {
      case diffDays === 0:
        return {
          ...commonProps,
          color: 'error',
          label: 'Expires today',
        };
      case diffDays > 0 && diffDays <= 7:
        return {
          ...commonProps,
          color: 'pending',
          label: 'Less than a week to expiration',
        };
      case diffDays > 7 && diffDays <= 30:
        return {
          ...commonProps,
          color: 'warning',
          label: 'Less than a month to expiration',
        };
      default:
        return {
          ...commonProps,
          color: 'action',
          label: 'Available',
        };
    }
  }, [diffDays]);
};

export const useExclusiveForAffiliates = () => {
  return useMemo(
    () => ({
      icon: <RemoveCircleIcon />,
      label: 'Exclusive for some affiliates',
      color: 'error',
      typographyColor: 'error',
    }),
    []
  );
};

export const useLocale = ({ value }) => {
  return useMemo(
    () => ({
      icon: <PublicIcon />,
      label: value,
      color: 'action',
    }),
    []
  );
};

export const useCPFlow = ({ value }) => {
  return useMemo(
    () => ({
      icon: <InfoOutlinedIcon />,
      label: value,
      color: 'action',
    }),
    []
  );
};

export const usePlatform = ({ value }) => {
  return useMemo(
    () => ({
      icon: <ComputerIcon />,
      label: value,
      color: 'action',
    }),
    []
  );
};

export const useConversionPagesCount = ({ value }) => {
  return useMemo(
    () => ({
      icon: <WebOutlinedIcon />,
      label: `${value} Conversion Pages`,
      shortLabel: value,
      color: 'action',
    }),
    []
  );
};

export const useAIGenerated = () => {
  return useMemo(
    () => ({
      icon: <AutoAwesomeIcon />,
      label: 'AI Generated',
      color: 'primary',
      typographyColor: 'primary',
    }),
    []
  );
};
